import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { AnimatePresence, motion } from 'framer-motion';

import { ReactComponent as LeftArrowSvg } from '../../../../image/LeftArrow.svg';
import { ReactComponent as EmptySignSvg } from '../../../../image/EmptySign.svg';
import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

export default function ClassScheduleCalendar({
  date,
  isRepeat,
  startDate,
  originalStartDate,
  endDate,
  rawEndDate,
  sunday, monday, tuesday, wednesday, thursday, friday, saturday,
  one, two, three, four, five,
}) {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [calendarViewMonth, setCalendarViewMonth] = useState(
    startDate ? moment(startDate, 'YYYY.MM.DD') : (date ? date.clone() : moment())
  );

  useEffect(() => {
    if (startDate) setCalendarViewMonth(moment(startDate, 'YYYY.MM.DD'));
  }, [startDate]);

  const calendarDateList = useMemo(() => {
    const firstWeek = calendarViewMonth.clone().startOf('month').week();
    const lastWeek = calendarViewMonth.clone().endOf('month').week() === 1 ? 53 : calendarViewMonth.clone().endOf('month').week();
    const list = [];
    for (let week = firstWeek; week <= lastWeek; week++) {
      for (let i = 0; i < 7; i++) {
        list.push(calendarViewMonth.clone().startOf('year').week(week).startOf('week').add(i, 'day'));
      }
    }
    return list;
  }, [calendarViewMonth]);

  const highlightedDates = useMemo(() => {
    const set = new Set();
    const start = startDate ? moment(startDate, 'YYYY.MM.DD') : null;
    // 반복 수업 컷오프 기준: 기존 일정 편집 시에는 originalStartDate(반복 첫 수업일)를, 신규 생성 시에는 startDate를 사용
    const repeatStart = originalStartDate ? moment(originalStartDate) : start;
    // rawEndDate(시간 포함 ISO)가 있으면 그대로 사용, 없으면 날짜만으로 하루 끝 시각으로 처리
    // NewWeeklyClass.js와 동일하게 isBefore로 비교 — T00:00:00이면 당일 제외, T23:59:59이면 당일 포함
    const end = endDate
      ? (rawEndDate ? moment(rawEndDate) : moment(endDate, 'YYYY.MM.DD').endOf('day'))
      : null;

    if (!isRepeat) {
      if (start && start.isValid()) set.add(start.format('YYYY-MM-DD'));
      return set;
    }
    const dayMap = [sunday, monday, tuesday, wednesday, thursday, friday, saturday];
    const selectedDays = dayMap.map((v, i) => v ? i : null).filter(i => i !== null);
    const weekMap = [one, two, three, four, five];
    const selectedWeeks = weekMap.map((v, i) => v ? i + 1 : null).filter(w => w !== null);
    const firstOfMonth = calendarViewMonth.clone().startOf('month');
    const viewMonth = calendarViewMonth.month();
    for (const dayIndex of selectedDays) {
      let daysUntilFirst = dayIndex - firstOfMonth.day();
      if (daysUntilFirst < 0) daysUntilFirst += 7;
      const firstOccurrence = firstOfMonth.clone().add(daysUntilFirst, 'days');
      for (const weekNum of selectedWeeks) {
        const target = firstOccurrence.clone().add((weekNum - 1) * 7, 'days');
        if (target.month() !== viewMonth) continue;
        if (repeatStart && repeatStart.isValid() && target.isBefore(repeatStart, 'day')) continue;
        if (end && !target.isBefore(end)) continue;
        set.add(target.format('YYYY-MM-DD'));
      }
    }
    return set;
  }, [calendarViewMonth, isRepeat, startDate, originalStartDate, endDate, rawEndDate, sunday, monday, tuesday, wednesday, thursday, friday, saturday, one, two, three, four, five]);

  const hasAnyDaySelected = sunday || monday || tuesday || wednesday || thursday || friday || saturday;
  const hasAnyWeekSelected = one || two || three || four || five;
  const isEmpty = isRepeat ? !(hasAnyDaySelected && hasAnyWeekSelected) : false;

  return (
    <TopLevelWrapper
      layout
      transition={{ layout: { duration: 0.3, ease: 'easeInOut' } }}>
      <AnimatePresence mode='wait' initial={false}>
        {isEmpty ? (
          <MotionSection
            key='empty'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}>
            <EmptyStateWrapper>
              <EmptyImage />
              <EmptyText>{t('classManage.calendarEmptyText')}</EmptyText>
            </EmptyStateWrapper>
          </MotionSection>
        ) : (
          <MotionSection
            key='calendar'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}>
            <Header>
              <MonthText>{calendarViewMonth.format('YY.MM')}</MonthText>
              <NavWrapper>
                <NavArrow onClick={() => setCalendarViewMonth(calendarViewMonth.clone().subtract(1, 'month'))}>
                  <LeftIcon />
                </NavArrow>
                <NavArrow onClick={() => setCalendarViewMonth(calendarViewMonth.clone().add(1, 'month'))}>
                  <RightIcon />
                </NavArrow>
              </NavWrapper>
            </Header>
            <CalendarWrapper>
              <DayHeader>
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <DayCol key={day}>{day}</DayCol>
                ))}
              </DayHeader>
              <DateGrid>
                {calendarDateList.map((d, i) => (
                  d.format('MM') === calendarViewMonth.format('MM')
                    ? <DateCell key={i} isHighlighted={highlightedDates.has(d.format('YYYY-MM-DD'))}>{d.format('D')}</DateCell>
                    : <DateOtherCell key={i}>{d.format('D')}</DateOtherCell>
                ))}
              </DateGrid>
            </CalendarWrapper>
          </MotionSection>
        )}
      </AnimatePresence>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;

  width: 272px;
  flex-shrink: 0;

  gap: 5px;

  overflow: hidden;
`;

const MotionSection = styled(motion.div)`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 5px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 250px;

  gap: 10px;

  box-sizing: border-box;
`;

const EmptyImage = styled(EmptySignSvg)`
  width: 161px;
  height: auto;

  & > rect {
    fill: ${props => props.theme.buttonFill3};
  }

  & > path {
    fill: ${props => props.theme.white};
  }

  & > g rect {
    fill: ${props => props.theme.line};
  }
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.line};
  text-align: center;

  margin: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const LabelText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
`;

const MonthText = styled.p`
  font-family: Poppins-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
`;

const NavWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const NavArrow = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  border-radius: 100px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LeftIcon = styled(LeftArrowSvg)`
  width: 6px;
  height: 10px;

  color: ${props => props.theme.text};
`;

const RightIcon = styled(LeftArrowSvg)`
  width: 6px;
  height: 10px;

  transform: rotate(180deg);
  color: ${props => props.theme.text};
`;

const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0 10px 10px 10px;

  box-sizing: border-box;
  width: 100%;
`;

const DayHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 38px;

  user-select: none;
`;

const DayCol = styled.p`
  display: flex;

  justify-content: center;
  align-items: center;

  flex: 1;

  font-family: NotoSansKr-Bold;
  font-size: 12px;

  color: ${props => props.theme.primary};

  margin: 0;
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);

  user-select: none;
  grid-auto-rows: 40px;

  width: 100%;
`;

const DateCell = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  justify-self: center;
  align-self: center;

  width: 30px;
  height: 30px;

  border-radius: 34px;
  background-color: ${props => props.isHighlighted ? props.theme.primaryAlpha50 : 'transparent'};

  font-family: NotoSansKr-Medium;
  font-size: 12px;

  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;
`;

const DateOtherCell = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  justify-self: center;
  align-self: center;

  width: 30px;
  height: 30px;

  font-family: NotoSansKr-Medium;
  font-size: 12px;

  color: ${props => props.theme.line};
`;
